// Firebase configuration and initialization for InfluenceHub MVP
// This file sets up Firebase services using the modern modular SDK v9+
// Exports named constants for Auth, Firestore, and Storage

import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  connectAuthEmulator,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence
} from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';

// Firebase configuration object
// Uses environment variables for security (required for Vercel deployment)
// Falls back to hardcoded values for local development only
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "AIzaSyCNfuqKeM_Vjn57H-kqNxUEgUxyipeCa1k",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "influencehub-d596e.firebaseapp.com",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "influencehub-d596e",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "influencehub-d596e.firebasestorage.app",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "561469577003",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "1:561469577003:web:cc81d070b7ba8e48b6176e",
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || "G-D6SE1WF8TZ"
};

// Initialize Firebase app
// This creates the main Firebase app instance
const app = initializeApp(firebaseConfig);

// Initialize Firebase Authentication
// Handles user login, signup, and session management
export const auth = getAuth(app);

// Set persistence at app initialization based on saved preference
// This runs BEFORE any auth state is restored, so it's critical for "Remember Me" to work
// The Login component will override this during login if the user changes the checkbox
// Use the same localStorage key as Login.jsx
const REMEMBER_ME_KEY = 'influencehub_remember_me';

if (typeof window !== 'undefined') {
  const rememberMePreference = localStorage.getItem(REMEMBER_ME_KEY);
  const shouldRemember = rememberMePreference === 'true';
  const persistence = shouldRemember ? browserLocalPersistence : browserSessionPersistence;
  
  // Set persistence immediately - this must happen before any auth state is restored
  // If no preference exists, default to session (user will be logged out on browser close)
  setPersistence(auth, persistence).then(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔐 [firebaseConfig] Auth persistence initialized to:', shouldRemember ? 'local (persists across browser restarts)' : 'session (cleared on tab close)');
      if (rememberMePreference === null) {
        console.log('   ℹ️ No saved preference found - defaulting to session. User can check "Remember Me" during login.');
      }
    }
  }).catch((error) => {
    console.warn('⚠️ [firebaseConfig] Failed to set auth persistence, falling back to session:', error);
    // Fallback to session if local fails (e.g., private browsing)
    setPersistence(auth, browserSessionPersistence).catch((fallbackError) => {
      console.error('❌ [firebaseConfig] Failed to set fallback persistence:', fallbackError);
    });
  });
} else {
  // Server-side: default to session persistence
  setPersistence(auth, browserSessionPersistence).catch(() => {
    // Ignore errors on server-side
  });
}

// Initialize Cloud Firestore
// NoSQL database for storing user data, campaigns, applications, etc.
export const db = getFirestore(app);

// Initialize Cloud Storage
// File storage for profile images, campaign media, and deliverables
export const storage = getStorage(app);

// Development mode: Connect to Firebase emulators (optional)
// Uncomment these lines to use local Firebase emulators during development
// if (process.env.NODE_ENV === 'development') {
//   connectAuthEmulator(auth, 'http://localhost:9099');
//   connectFirestoreEmulator(db, 'localhost', 8080);
//   connectStorageEmulator(storage, 'localhost', 9199);
// }

// Export the app instance as default
export default app;
